import { ApolloClient, ApolloLink, fromPromise, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { t } from '@lingui/macro';
import introspectionResult from '../generated/gql/schema.json';
import { withScalars } from 'apollo-link-scalars';
import { createUploadLink } from 'apollo-upload-client';
import type { IntrospectionQuery } from 'graphql';
import { buildClientSchema, GraphQLScalarType } from 'graphql';
import type { ErrorReportEntry, ErrorReportInput } from './formatErrorReport';
import { buildErrorReportFromGraphQLErrors } from './formatErrorReport';

const useIncomingArray = <B extends unknown[] = []>(_: unknown, incoming: B[] = []) => incoming;

const omitTypename = <K extends string, V>(key: K, value: V) =>
  key === '__typename' ? undefined : value;
/**
 * We omit the typename by default, because mutations don't expect the typename to be included.
 * To add back the typename, use the `includeTypename` context option.
 */
const createOmitTypenameLink = () => {
  return new ApolloLink((operation, forward) => {
    if (operation.getContext().includeTypename) return forward(operation);
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    return forward(operation);
  });
};

export const createApolloClient = (
  uri: string,
  fetchToken: (forceRefresh?: boolean) => Promise<string | null>,
  onGlobalError: (message: string, diagnostics?: ErrorReportInput) => void,
  onAuthRequired: () => void,
) => {
  const omitTypenameLink = createOmitTypenameLink();

  const httpLink = createUploadLink({
    uri,
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await fetchToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
      noInitialToken: !token,
    };
  });

  // Global error handler. Surfaces a generic banner for any unhandled
  // GraphQL/network error and transparently retries once with a refreshed
  // access token when the server reports an expired token.
  // Per-operation opt-out: pass `context: { suppressGlobalError: true }`.
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const { suppressGlobalError, retriedForAuth, noInitialToken } = operation.getContext();

    const isUnauthenticatedGraphQL = graphQLErrors?.some(
      (err) => err.extensions?.code === 'UNAUTHENTICATED',
    );
    const isUnauthenticatedNetwork =
      !!networkError && 'statusCode' in networkError && networkError.statusCode === 401;
    const isUnauthenticated = isUnauthenticatedGraphQL || isUnauthenticatedNetwork;

    // No valid session at all — skip the silent-refresh dance (Auth0 will
    // just throw "Consent required") and kick off an interactive login.
    if (isUnauthenticated && noInitialToken) {
      onAuthRequired();
    } else if (isUnauthenticated && !retriedForAuth) {
      return fromPromise(fetchToken(true).catch(() => null)).flatMap((token) => {
        if (!token) {
          // Refresh failed for a session we thought was good — kick off
          // an interactive login and let the original 401 propagate.
          onAuthRequired();
          operation.setContext({ retriedForAuth: true });
          return forward(operation);
        }
        operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
          headers: { ...headers, authorization: `Bearer ${token}` },
          retriedForAuth: true,
        }));
        return forward(operation);
      });
    }

    if (suppressGlobalError) return;
    if (graphQLErrors?.length || networkError) {
      const report = buildErrorReportFromGraphQLErrors(graphQLErrors ?? [], {
        operationName: operation.operationName,
      });
      if (networkError) {
        const networkEntry: ErrorReportEntry = {
          message: networkError.message || 'Network error',
          code:
            'statusCode' in networkError && typeof networkError.statusCode === 'number'
              ? `NETWORK / ${networkError.statusCode}`
              : 'NETWORK',
        };
        report.errors.push(networkEntry);
      }
      onGlobalError(t`Something went wrong, please try again later.`, report);
    }
  });

  // Apollo-client has no built-in support for custom scalars, so we are using
  // apollo-link-scalars to introduce Date and DateTime as custom scalars.
  // See https://github.com/apollographql/apollo-client/issues/8857
  const schema = buildClientSchema(introspectionResult as unknown as IntrospectionQuery);

  const typesMap = {
    Date: new GraphQLScalarType({
      name: 'Date',
      serialize: (parsed?: Date) => parsed?.toISOString().substring(0, 10),
      parseValue: (raw: any) => raw && new Date(raw),
    }),
    DateTime: new GraphQLScalarType({
      name: 'DateTime',
      serialize: (parsed?: Date) => parsed?.toISOString(),
      parseValue: (raw: any) => raw && new Date(raw),
    }),
    URL: new GraphQLScalarType({
      name: 'URL',
      serialize: (parsed: URL) => parsed.toString(),
      parseValue: (raw: any) => new URL(raw),
    }), // few
  };
  const link = ApolloLink.from([
    errorLink,
    authLink,
    withScalars({ schema, typesMap }),
    omitTypenameLink,
    httpLink,
  ]);

  // When we receive a new array, always replace the existing array.
  const replace = (_existing: any[], incoming: any[]) => incoming;

  return new ApolloClient({
    link,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-and-network',
      },
    },
    cache: new InMemoryCache({
      typePolicies: {
        ShareSeries: {
          // The ['id'] item indicates that the id field of the previous field in the array (shareType) is part of the cache ID.
          keyFields: ['from', 'to', 'shareType', ['id']],
        },
        ShareBatch: {
          keyFields: ['shareSeries', ['from', 'to', 'shareType', ['id']]],
        },
        Document: {
          merge: true,
          fields: {
            labels: {
              merge: useIncomingArray,
            },
          },
        },
        RegisterMutation: {
          merge: true,
          fields: {
            administration: {
              merge: useIncomingArray,
            },
            conversion: {
              merge: useIncomingArray,
            },
            endAdministration: {
              merge: useIncomingArray,
            },
            endPledge: {
              merge: useIncomingArray,
            },
            endUsufruct: {
              merge: useIncomingArray,
            },
            incorporation: {
              merge: useIncomingArray,
            },
            issuance: {
              merge: useIncomingArray,
            },
            onboarding: {
              merge: useIncomingArray,
            },
            payingUp: {
              merge: useIncomingArray,
            },
            pledge: {
              merge: useIncomingArray,
            },
            transfer: {
              merge: useIncomingArray,
            },
            usufruct: {
              merge: useIncomingArray,
            },
            withdrawal: {
              merge: useIncomingArray,
            },
          },
        },
        Register: {
          merge: true,
          fields: {
            mutations: {
              merge: useIncomingArray,
            },
            documents: {
              merge: useIncomingArray,
            },
          },
        },
        Query: {
          fields: {
            invitations: { merge: replace },
          },
        },
      },
    }),
  });
};
